import { useEffect, useState } from "react";
import { Edit, Trash2, Download, X, Save, Cake, Mail, Phone, MapPin, AlertTriangle } from "lucide-react";
import api, { formatPrice, formatApiError, API } from "../../lib/api";
import { toast } from "sonner";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [birthdays, setBirthdays] = useState([]);

  const load = () => api.get("/admin/customers").then(r => setCustomers(r.data || []));
  const loadBirthdays = () => api.get("/admin/customers/birthdays?days_ahead=2")
    .then(r => setBirthdays(r.data || [])).catch(() => {});

  useEffect(() => { load(); loadBirthdays(); }, []);

  const filtered = customers.filter(c =>
    !filter || [c.email, c.name, c.surname, c.phone, c.city]
      .filter(Boolean).join(" ").toLowerCase().includes(filter.toLowerCase())
  );

  const save = async () => {
    try {
      await api.put(`/admin/customers/${editing.id}`, {
        name: editing.name || "",
        surname: editing.surname || "",
        email: editing.email,
        phone: editing.phone || "",
        address: editing.address || "",
        postal_code: editing.postal_code || "",
        city: editing.city || "",
        country: editing.country || "",
        birthdate: editing.birthdate || "",
      });
      toast.success("Cliente actualizado");
      setEditing(null);
      load();
      loadBirthdays();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`¿Eliminar al cliente "${name}"? Esta acción es irreversible.`)) return;
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-customers">
      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="overline mb-3">CRM</p>
          <h1 className="font-heading text-4xl text-ivory">Clientes</h1>
          <p className="text-sm text-ivory/60 mt-3">{customers.length} cliente{customers.length !== 1 && "s"} registrado{customers.length !== 1 && "s"}</p>
        </div>
        <a href={`${API}/admin/customers/export.csv`} download className="btn-gold !py-2 !px-4 flex items-center gap-2" data-testid="export-customers-btn">
          <Download size={14} /> Exportar listado completo
        </a>
      </div>

      {/* Birthday alerts */}
      {birthdays.length > 0 && (
        <div className="bg-graphite border border-gold mb-8 p-6" data-testid="birthday-alerts">
          <div className="flex items-center gap-3 mb-4">
            <Cake size={22} className="text-gold" />
            <h2 className="font-heading text-2xl text-ivory">Cumpleaños próximos</h2>
            <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-gold border border-gold px-2 py-1">{birthdays.length} alerta{birthdays.length !== 1 && "s"}</span>
          </div>
          <p className="text-sm text-ivory/60 mb-5">Envía un regalo o detalle especial a estos clientes en los próximos 2 días.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {birthdays.map(b => (
              <div key={b.id} className="bg-carbon border border-gold/30 p-4" data-testid={`birthday-${b.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-heading text-lg text-ivory">{b.name} {b.surname}</p>
                    <p className="text-xs text-ivory/60">{b.birthdate}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${
                    b.days_until === 0 ? "border-gold text-gold bg-gold/10" : "border-rose/50 text-rose"
                  }`}>
                    {b.days_until === 0 ? "¡Hoy!" : `${b.days_until} día${b.days_until !== 1 ? "s" : ""}`}
                  </span>
                </div>
                <div className="text-xs space-y-1 text-ivory/70">
                  <div className="flex items-center gap-2"><Mail size={11} /> <a href={`mailto:${b.email}`} className="hover:text-gold">{b.email}</a></div>
                  {b.phone && <div className="flex items-center gap-2"><Phone size={11} /> {b.phone}</div>}
                  {b.address && <div className="flex items-center gap-2"><MapPin size={11} /> {b.address}, {b.city}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <input
        data-testid="customer-search"
        placeholder="Buscar por nombre, email, ciudad..."
        value={filter} onChange={e => setFilter(e.target.value)}
        className="luxe-input border border-smoke px-4 py-3 mb-6 max-w-md"
      />

      <div className="bg-graphite border border-smoke overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr>
              <th>Nombre completo</th>
              <th>Contacto</th>
              <th>Dirección</th>
              <th>Nacimiento</th>
              <th>Pedidos</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} data-testid={`customer-${c.id}`}>
                <td>
                  <p className="text-ivory">{c.name || "—"} {c.surname || ""}</p>
                  <p className="text-[10px] text-ivory/40 mt-1">Alta: {c.created_at ? new Date(c.created_at).toLocaleDateString("es-ES") : "-"}</p>
                </td>
                <td className="text-xs text-ivory/80">
                  <p>{c.email}</p>
                  {c.phone && <p className="text-ivory/50">{c.phone}</p>}
                </td>
                <td className="text-xs text-ivory/70">
                  {c.address ? (
                    <>
                      <p>{c.address}</p>
                      <p className="text-ivory/50">{c.postal_code} {c.city} {c.country && `· ${c.country}`}</p>
                    </>
                  ) : <span className="text-ivory/30">—</span>}
                </td>
                <td className="text-ivory/70 text-xs">{c.birthdate || <span className="text-ivory/30">—</span>}</td>
                <td className="text-ivory/70">{c.order_count}</td>
                <td className="text-gold">{formatPrice(c.total_spent)}</td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...c })} className="text-ivory/60 hover:text-gold" data-testid={`edit-cust-${c.id}`} title="Editar">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => del(c.id, `${c.name || ""} ${c.surname || ""}`.trim() || c.email)} className="text-ivory/60 hover:text-rose" data-testid={`del-cust-${c.id}`} title="Eliminar">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="7" className="text-center text-ivory/40 py-12">Sin clientes</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-carbon/90 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-graphite border border-smoke max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-smoke flex justify-between">
              <h2 className="font-heading text-2xl text-ivory">Editar cliente</h2>
              <button onClick={() => setEditing(null)} className="text-ivory hover:text-gold"><X size={20} /></button>
            </div>
            <form onSubmit={e => { e.preventDefault(); save(); }} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="luxe-label">Nombre</label><input data-testid="cust-name" value={editing.name || ""} onChange={e => setEditing({ ...editing, name: e.target.value })} className="luxe-input" /></div>
                <div><label className="luxe-label">Apellidos</label><input data-testid="cust-surname" value={editing.surname || ""} onChange={e => setEditing({ ...editing, surname: e.target.value })} className="luxe-input" /></div>
                <div><label className="luxe-label">Email</label><input type="email" required value={editing.email || ""} onChange={e => setEditing({ ...editing, email: e.target.value })} className="luxe-input" /></div>
                <div><label className="luxe-label">Teléfono</label><input data-testid="cust-phone" value={editing.phone || ""} onChange={e => setEditing({ ...editing, phone: e.target.value })} className="luxe-input" /></div>
                <div className="col-span-2"><label className="luxe-label">Dirección</label><input data-testid="cust-address" value={editing.address || ""} onChange={e => setEditing({ ...editing, address: e.target.value })} className="luxe-input" /></div>
                <div><label className="luxe-label">Código postal</label><input data-testid="cust-postal" value={editing.postal_code || ""} onChange={e => setEditing({ ...editing, postal_code: e.target.value })} className="luxe-input" /></div>
                <div><label className="luxe-label">Población</label><input data-testid="cust-city" value={editing.city || ""} onChange={e => setEditing({ ...editing, city: e.target.value })} className="luxe-input" /></div>
                <div><label className="luxe-label">País</label><input value={editing.country || ""} onChange={e => setEditing({ ...editing, country: e.target.value })} className="luxe-input" /></div>
                <div>
                  <label className="luxe-label">Fecha de nacimiento</label>
                  <input data-testid="cust-bday" type="date" value={editing.birthdate || ""} onChange={e => setEditing({ ...editing, birthdate: e.target.value })} className="luxe-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-smoke">
                <button type="submit" className="btn-gold flex items-center gap-2" data-testid="cust-save"><Save size={14} /> Guardar cambios</button>
                <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
